/*
`DataView`'s context system works better with vanilla contexts than `makeReactContext`. Vanilla contexts are
fine in modern React and the fixes in `makeReactContexts` are irrelevant to the inner workings of data view.
It also causes issues by doing things that are now bad practice (specifically, the render side-effect with its
`useState` - this makes `DataView` crash when changing `analysisView`.)
*/
import {DataTestAttrs} from '@benchling/js-test-utils';
import {createContext, isValidElement, useContext} from 'react';

import {BlockConfig} from 'analysis-tool/data-layout/DataLayoutTypes';
import {assert} from 'Asserts';
import {Chart} from 'charts/ChartTypes';
import {ActionOrigin} from 'constants/AnalyticsConstants';
import {DataFrameHeaderMenuOptions} from 'data-frames/header-menu/DataFrameHeaderMenuOptions';
import {PersistedDataFrameMetadataModalProps} from 'data-frames/modals/PersistedDataFrameMetadataModal';
import {DataFrameRenderOverrides} from 'data-frames/table/DataFrameTable';
import {DataFrameTableMode} from 'data-frames/table/DataFrameTableConstants';
import {PersistedDataFrameGraphQL} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStep, PipelineViewGraphQL} from 'pipelines/PipelineTypes';
import {getDefaultClientIdForView} from 'pipelines/PipelineViewUtils';
import makeUseGlobalClientState from 'ui-library/hooks/global-client-state/makeUseGlobalClientState';
import {AsyncStatus} from 'ui-library/hooks/useAsyncState';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MakeFieldsNonOptional} from 'util/Types';

import {getTitleForView} from './AnalysisViewUtils';

export interface PreviewDraftStepData {
  chartPreviews: Array<Chart>;
  errorMessage?: string;
  previewStatus: AsyncStatus;
}
export interface PreviewData {
  draftStep: PipelineStep;
  previewDraftStepData: PreviewDraftStepData;
  setPreviewDraftStepData: (previewDraftStepData: PreviewDraftStepData) => void;
}

export type DataViewBlockConfig = BlockConfig & {
  // Some components need different styling for layouts.
  isInLayout: boolean;
};

/**
 * The configurations available to `DataViewContext`.
 */
export interface DataViewContextValue {
  /**
   * The `actionOrigin` the `DataView` to association analytics events with.
   */
  actionOrigin?: ActionOrigin;
  /**
   * Specify the actions to put in the header of the `DataView`.
   *
   * 2 types of actions:
   *  - `buttonActions`: Actions that are displayed as buttons in the toolbar.
   *  - `overflowActions`: Actions that are displayed in an overflow menu of the toolbar.
   *
   * Actions can be specified as either constant values or functions. When specified as a function, it will be called
   * with the `actionInput` to generate the action. This is useful when you need information from lower-level
   * components that `actionInput` provides. Since `actionInput` is not guaranteed to be provided, callers should
   * implement fallback logic.
   *
   * If an action does not require `actionInput`, it is simpler to specify it as a constant value.
   *
   * Internal actions will be added to these actions, so the specified list will not be the final list.
   * Internal actions are always added at the end of the list like so: [...External actions, ...Internal actions].
   *
   * @see `DataViewAction` for more details on the actions available.
   */
  actions?: {
    /**
     * The actions to display as buttons in the toolbar.
     *
     * They may either be specified as regular actions or custom button components.
     */
    buttonActions: Array<ButtonDataViewAction | CustomDataViewButton>;
    /**
     * The actions to display in an overflow menu of the toolbar.
     */
    overflowActions: Array<OverflowDataViewAction>;
  };
  /**
   * The input to pass to the `onClick` of the actions.
   */
  actionInput?: DataViewActionInput;
  /**
   * Additional metadata content to display in the metadata modal.
   *
   * Metadata is rendered as a grid of metadata in a modal. Views have internal metadata, but this function
   * allows for callers to add additional metadata
   *
   * TODO(BNCH-123663): Metadata is currently only supported for data frame views.
   */
  additionalMetadataContent?: {
    /**
     * Additional data to show in the grid of metadata. Each entry will get half of a row (or a full row
     * on small screens).
     *
     * @note With current usage, React doesn't seem to mind if each element doesn't have a `key` prop.
     *
     * @example
     * ```
     * [
     *   {
     *     component: (
     *       <FormGroup>
     *         <FieldLabel label="Creator" />
     *         <p>{persistedDataFrame.creator.name}</p>
     *       </FormGroup>
     *     ),
     *     key: 'Creator',
     *   },
     * ]
     * ```
     */
    additionalGridCells: PersistedDataFrameMetadataModalProps['additionalGridCells'];
    /**
     * Additional element to show underneath the grid of metadata.
     *
     * @example <pre>{sql query that created the dataframe}</pre>
     */
    additionalContent: PersistedDataFrameMetadataModalProps['additionalContent'];
  };
  /**
   * The view to render.
   *
   * This is optional when using standalone view components (ChartView, DataFrameView, FileView)
   * with direct props. When using DataView, this should be provided for automatic view resolution.
   */
  analysisView?: PipelineViewGraphQL;
  /**
   * The client id for the current view.
   */
  clientId?: string;
  /**
   * Configuration options that only used when rendering a data frames.
   */
  dataFrameViewOptions?: {
    dataFrameRenderOverrides?: DataFrameRenderOverrides;
    defaultTableMode?: DataFrameTableMode;
    headerMenuActions?: (input: DataViewActionInput) => DataFrameHeaderMenuOptions | null;
  };
  /**
   * Data test attributes to be passed to the Pane component.
   *
   * Used to differentiate between different data view types in browser tests.
   */
  dataTestAttrs?: DataTestAttrs;
  /**
   * Callback for when the title is updated.
   *
   * Callback is guaranteed to only be called with titles that are different from the current title.
   *
   * If unspecified, doesn't render title as editable.
   */
  onTitleChange?: (newTitle: string) => void;
  /**
   * The data to display as a the preview.
   */
  previewData?: PreviewData;
  /**
   * The state of the currently selected rows.
   *
   * If `null`, it means rows can't be selected in the current view (i.e. because there's no data frame).
   */
  rowSelectionState?: {
    selectedRowIndices: Set<number>;
    setSelectedRowIndices: (selectedRowIndices: Set<number>) => void;
  };
  /**
   * The title to display for the view.
   *
   * For default behavior @see getTitleForView
   */
  title?: string;
  /**
   * The warning to display to the user about the view, consisting of two parts:
   *   1. A label displayed directly without any interaction.
   *   2. A message displayed on hover.
   */
  warning?: DataViewWarning | null;

  /**
   * The header to display for the view if its in a layout.
   */
  blockConfig?: DataViewBlockConfig;

  /**
   * Whether the view is a chart view, we have to do special styling for charts in a layout because we
   * need to set the height/width for plotly.
   */
  isChartView?: boolean;
}

/**
 * Action used by `DataViewContextValue`'s `buttonActions` to display as a button in the toolbar.
 *
 * @see `DataViewContextValue` for more details on how actions are specified for `DataView`.
 */
export type ButtonDataViewAction = DataViewAction<ButtonDataViewActionInterface>;
export type CustomDataViewButton = JSX.Element | null;

export interface ButtonDataViewActionInterface extends DataViewActionInterface {
  /**
   * Whether the action should be displayed in an alternative style.
   *
   * Currently only supported for button actions
   */
  isAlternativeButtonColor?: boolean;
  /**
   * The main text to display with the action.
   */
  label?: string;
}

/**
 * Action used by `DataViewContextValue`'s `overflowActions` to display in an overflow menu.
 *
 * @see `DataViewContextValue` for more details on how actions are specified for `DataView`.
 */
export type OverflowDataViewAction = DataViewAction<OverflowDataViewActionInterface>;

interface OverflowDataViewActionInterface extends DataViewActionInterface {
  /**
   * The main text to display with the action.
   */
  label: string;
  isBeta?: boolean;
}

export function isButtonActionCustomButton(
  buttonAction: ButtonDataViewAction | CustomDataViewButton
): buttonAction is CustomDataViewButton {
  return isValidElement(buttonAction) || buttonAction == null;
}

export function isDataViewActionConstantType<TDataViewAction extends DataViewActionInterface>(
  action: TDataViewAction | DataViewActionGenerator<TDataViewAction>
): action is Exclude<TDataViewAction, DataViewActionGenerator<TDataViewAction>> {
  return typeof action !== 'function';
}

export function assertDataViewActionIsConstant<TDataViewAction extends DataViewActionInterface>(
  action: TDataViewAction | DataViewActionGenerator<TDataViewAction>
): asserts action is Exclude<TDataViewAction, DataViewActionGenerator<TDataViewAction>> {
  assert(isDataViewActionConstantType(action), 'Expected action to be a constant');
}

export function isDataViewActionFunctionType<TDataViewAction extends DataViewActionInterface>(
  action: TDataViewAction | DataViewActionGenerator<TDataViewAction>
): action is DataViewActionGenerator<TDataViewAction> {
  return typeof action === 'function';
}

export function assertDataViewActionIsFunction<TDataViewAction extends DataViewActionInterface>(
  action: TDataViewAction | DataViewActionGenerator<TDataViewAction>
): asserts action is DataViewActionGenerator<TDataViewAction> {
  assert(isDataViewActionFunctionType(action), 'Expected action to be a function');
}

type DataViewAction<TDataViewAction extends DataViewActionInterface> =
  | TDataViewAction
  | DataViewActionGenerator<TDataViewAction>;

type DataViewActionGenerator<TDataViewAction extends DataViewActionInterface> = (
  input: DataViewActionInput
) => TDataViewAction | null;

/**
 * The base interface for actions available for `DataView`.
 */
interface DataViewActionInterface {
  /**
   * Whether the action should be displayed as disabled.
   *
   * Will show special styling and remove the ability to click the action
   */
  isDisabled?: boolean;
  /**
   * Whether the action should be displayed as toggled.
   *
   * Toggle is a special style that looks like the action is selected. This can be used along
   * with state management by the parent to implement toggle actions.
   */
  isToggled?: boolean;
  /**
   * Secondary text to display with the action to help the user understand the action.
   */
  helpText?: string;
  /**
   * The icon to display with the action.
   */
  icon: BenchlingIconName;
  /**
   * The function to call when the action is clicked.
   */
  onClick: () => void;
}

/**
 * Type describing the input to pass to action generators.
 */
export interface DataViewActionInput {
  persistedDataFrame?: PersistedDataFrameGraphQL;
  // Add chart, file, ect. as needed. This will required separating the querying vs. non-quering components for those views.
}

export interface DataViewWarning {
  label: string;
  hoverMessage: string;
}

export function combineDataViewWarnings(
  previousWarning: DataViewWarning | null | undefined,
  newWarning: DataViewWarning | null | undefined
): DataViewWarning | null | undefined {
  if (previousWarning == null) {
    return newWarning;
  } else if (newWarning == null) {
    return previousWarning;
  } else {
    return {
      label: `${previousWarning.label}. ${newWarning.label}`,
      hoverMessage: `${previousWarning.hoverMessage}. ${newWarning.hoverMessage}`,
    };
  }
}

type FieldsSetByHook = 'clientId' | 'rowSelectionState' | 'title';
type FieldsWithDefaults =
  | FieldsSetByHook
  | 'actionOrigin'
  | 'actions'
  | 'actionInput'
  | 'dataFrameViewOptions'
  | 'dataTestAttrs'
  | 'warning';

const DEFAULT_DATA_VIEW_CONTEXT_VALUE: Omit<
  MakeFieldsNonOptional<Pick<DataViewContextValue, FieldsWithDefaults>, FieldsWithDefaults>,
  FieldsSetByHook
> = {
  actionOrigin: ActionOrigin.UI_UNDETERMINED,
  actions: {
    buttonActions: [],
    overflowActions: [],
  },
  actionInput: {},
  dataFrameViewOptions: {},
  dataTestAttrs: {component: 'DataViewPane', element: 'root'},
  warning: null,
};

const DEFAULT_CLIENT_ID = 'data-view-default-client-id';

/**
 * Context used to configure `DataView`'s behavior.
 *
 * @see `DataViewContextValue` for more details on the configurations available
 */
const DataViewContext = createContext<DataViewContextValue>(undefined as never);

export default DataViewContext;

export const rowSelectionState = makeUseGlobalClientState<Set<number>>(new Set<number>());

export const DEFAULT_BLOCK_CONFIG: DataViewBlockConfig = {
  blockMode: 'PANE',
  title: null,
  subtitle: null,
  infoText: null,
  isCollapsible: null,
  isInLayout: false,
};

export function useDataViewContext(): MakeFieldsNonOptional<DataViewContextValue, FieldsWithDefaults> {
  const maybeContext = useContext(DataViewContext);

  const context = maybeContext ?? {};

  const {analysisView} = context;

  const clientId = analysisView != null ? getDefaultClientIdForView(analysisView) : DEFAULT_CLIENT_ID;

  const [selectedRowIndices, setSelectedRowIndices] = rowSelectionState.useGlobalClientState(clientId);

  const title = analysisView != null ? getTitleForView(analysisView) : 'Data View';

  const defaultContextValue = {
    ...DEFAULT_DATA_VIEW_CONTEXT_VALUE,
    clientId,
    rowSelectionState: {
      selectedRowIndices,
      setSelectedRowIndices,
    },
    title,
  };

  return {
    ...defaultContextValue,
    ...context,
  };
}
